import { useAui, useAuiState } from '@assistant-ui/react';
import { XIcon } from 'lucide-react';
import {
  useEffect,
  useRef,
  type FC,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent
} from 'react';
import DomainFavicon from '@/app/common/components/DomainFavicon';
import { cn } from '@/lib/utils';
import {
  appendDomainMentionsToMessage,
  domainMentionLabel
} from '../utils/domainMentionMarkers';
import { useOptionalMcpComposerMentions } from './McpComposerMentionsContext';

function getComposerApi(aui: ReturnType<typeof useAui>) {
  const composer =
    typeof (aui as { composer?: unknown }).composer === 'function'
      ? (
          aui as {
            composer: () => {
              setText: (value: string) => void;
              send: () => void;
            };
          }
        ).composer()
      : (
          aui as {
            composer: {
              setText: (value: string) => void;
              send: () => void;
            };
          }
        ).composer;
  return composer;
}

function focusComposerInput(selectAll = false) {
  window.requestAnimationFrame(() => {
    const input = document.querySelector<HTMLTextAreaElement>(
      '.aui-composer-input, textarea[aria-label="Message input"]'
    );
    if (!input) return;
    input.focus();
    if (selectAll) {
      input.select();
    } else {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    }
  });
}

/**
 * Inline domain mention chips: leading text + badges, then the composer
 * input continues after them (RTL: text → badge → continue typing).
 */
export const McpComposerDomainBadges: FC = () => {
  const aui = useAui();
  const text = useAuiState(s => s.composer.text);
  const mentions = useOptionalMcpComposerMentions();
  const prefixRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const input = document.querySelector<HTMLTextAreaElement>(
      '.aui-composer-input, textarea[aria-label="Message input"]'
    );
    if (!input) return;
    const hasChips = Boolean(mentions?.domainMentions.length);
    if (hasChips) {
      if (!input.dataset.mcpPh) {
        input.dataset.mcpPh = input.getAttribute('placeholder') ?? '';
      }
      input.setAttribute('placeholder', '');
    } else if (input.dataset.mcpPh != null) {
      input.setAttribute('placeholder', input.dataset.mcpPh);
      delete input.dataset.mcpPh;
    }
  }, [mentions?.domainMentions.length]);

  // Keep contentEditable DOM in sync when leading text is set programmatically.
  useEffect(() => {
    const el = prefixRef.current;
    if (!el || !mentions) return;
    if (el.textContent !== mentions.textBeforeMentions) {
      el.textContent = mentions.textBeforeMentions;
    }
  }, [mentions, mentions?.textBeforeMentions]);

  // Ctrl/Cmd+A → flatten chips into one editable textarea and select all.
  // Backspace at start of input → remove last badge (then restore leading text).
  useEffect(() => {
    if (!mentions?.domainMentions.length) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const inComposer = Boolean(
        target?.closest?.('.aui-composer-root, .aui-composer-input')
      );
      const inPrefix = Boolean(
        target?.closest?.('.mcp-domain-mention-prefix')
      );
      if (!inComposer && !inPrefix) return;

      const isSelectAll =
        (event.key === 'a' || event.key === 'A') &&
        (event.ctrlKey || event.metaKey);
      if (isSelectAll) {
        event.preventDefault();
        event.stopPropagation();
        const input = document.querySelector<HTMLTextAreaElement>(
          '.aui-composer-input, textarea[aria-label="Message input"]'
        );
        const merged = mentions.flattenToPlainText(input?.value ?? '');
        try {
          getComposerApi(aui).setText(merged);
        } catch {
          /* ignore */
        }
        focusComposerInput(true);
        return;
      }

      if (event.key !== 'Backspace' || event.isComposing) return;
      const input = document.querySelector<HTMLTextAreaElement>(
        '.aui-composer-input, textarea[aria-label="Message input"]'
      );
      if (!input || target !== input) return;
      if (input.selectionStart !== 0 || input.selectionEnd !== 0) return;

      const last = mentions.domainMentions[mentions.domainMentions.length - 1];
      if (!last) return;
      event.preventDefault();
      event.stopPropagation();
      const restore = mentions.removeDomainMention(last.slug);
      if (restore !== null) {
        const after = input.value.replace(/\u200b/g, '');
        try {
          getComposerApi(aui).setText(`${restore}${after}`);
        } catch {
          /* ignore */
        }
        focusComposerInput(false);
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [aui, mentions, mentions?.domainMentions]);

  if (!mentions) return null;
  if (!mentions.domainMentions.length && !mentions.textBeforeMentions) {
    return null;
  }

  const remove = (slug: string) => {
    const restore = mentions.removeDomainMention(slug);
    if (restore !== null) {
      const after = text.replace(/\u200b/g, '');
      const merged = `${restore}${after}`.replace(/\u200b/g, '');
      try {
        getComposerApi(aui).setText(merged);
      } catch {
        /* ignore */
      }
      focusComposerInput(false);
    }
  };

  const syncPrefixFromDom = (el: HTMLSpanElement) => {
    mentions.setTextBeforeMentions(el.textContent ?? '');
  };

  return (
    <>
      {mentions.domainMentions.length > 0 || mentions.textBeforeMentions ? (
        <span
          ref={prefixRef}
          // Same padding/font/line-height utilities as the composer textarea so
          // the leading text sits on the textarea's first line.
          className="mcp-domain-mention-prefix whitespace-pre-wrap py-2.5 text-[15px] leading-6 outline-none"
          contentEditable={mentions.domainMentions.length > 0}
          suppressContentEditableWarning
          role={mentions.domainMentions.length > 0 ? 'textbox' : undefined}
          aria-label="متن قبل از دامنه"
          onInput={(e: FormEvent<HTMLSpanElement>) => {
            syncPrefixFromDom(e.currentTarget);
          }}
          onBlur={(e: FormEvent<HTMLSpanElement>) => {
            syncPrefixFromDom(e.currentTarget);
          }}
          onKeyDown={(e: ReactKeyboardEvent<HTMLSpanElement>) => {
            // Allow Ctrl+A inside prefix to flatten whole composer line.
            if ((e.key === 'a' || e.key === 'A') && (e.ctrlKey || e.metaKey)) {
              return; // handled by document capture listener
            }
            e.stopPropagation();
          }}
        />
      ) : null}
      {mentions.domainMentions.map(domain => {
        const address = domain.url?.trim() || 'دامنه';
        const showName = domain.show_name?.trim() || null;
        const label = domainMentionLabel(domain);
        return (
          <span
            key={domain.slug}
            className={cn(
              'mcp-domain-mention-badge group/badge cursor-pointer',
              'bg-sky-500/15 text-sky-700 dark:bg-sky-400/15 dark:text-sky-300',
              // h-6 + mt-2.5 = the textarea's first 24px line after its py-2.5
              'mt-2.5 inline-flex h-6 max-w-[18rem] shrink-0 items-center gap-1.5 rounded-full',
              'px-2 py-0.5 text-xs font-medium leading-5',
              'ring-1 ring-inset ring-sky-500/20 dark:ring-sky-400/25'
            )}
            dir="ltr"
            title={label}
          >
            <button
              type="button"
              aria-label={`حذف ${label}`}
              title="حذف"
              className={cn(
                'relative inline-flex size-3.5 shrink-0 items-center justify-center rounded-full',
                '!border-0 !bg-transparent !p-0 !shadow-none',
                'text-sky-700 hover:bg-sky-500/25 hover:text-sky-900',
                'dark:text-sky-300 dark:hover:bg-sky-400/25 dark:hover:text-white'
              )}
              onMouseDown={e => e.preventDefault()}
              onClick={() => remove(domain.slug)}
            >
              <span className="inline-flex group-hover/badge:hidden group-focus-within/badge:hidden">
                <DomainFavicon
                  domain={domain.url}
                  width={14}
                  className="rounded-full flex-shrink-0"
                />
              </span>
              <XIcon
                className="size-3.5 hidden group-hover/badge:block group-focus-within/badge:block"
                strokeWidth={2.5}
                aria-hidden
              />
            </button>
            <span className="flex min-w-0 items-center gap-1">
              <span className="truncate">{address}</span>
              {showName ? (
                <span className="shrink-0 opacity-75">({showName})</span>
              ) : null}
            </span>
          </span>
        );
      })}
    </>
  );
};

/**
 * Before send, folds leading text + domain mentions into the composer message.
 */
export const McpDomainMentionSendBridge: FC = () => {
  const aui = useAui();
  const text = useAuiState(s => s.composer.text);
  const mentions = useOptionalMcpComposerMentions();
  const textRef = useRef(text);
  const leadingRef = useRef(mentions?.textBeforeMentions ?? '');
  const domainsRef = useRef(mentions?.domainMentions ?? []);

  textRef.current = text;
  leadingRef.current = mentions?.textBeforeMentions ?? '';
  domainsRef.current = mentions?.domainMentions ?? [];

  useEffect(() => {
    if (!mentions) return;

    const mergeAndSend = () => {
      const domains = domainsRef.current;
      if (!domains.length) return false;
      try {
        const composer = getComposerApi(aui);
        const body = `${leadingRef.current}${textRef.current.replace(/\u200b/g, '')}`;
        const next = appendDomainMentionsToMessage(body, domains);
        composer.setText(next);
        mentions.clearDomainMentions();
        window.requestAnimationFrame(() => {
          try {
            getComposerApi(aui).send();
          } catch (error) {
            console.error('Failed to send after domain mention merge', error);
          }
        });
        return true;
      } catch (error) {
        console.error('Failed to merge domain mentions before send', error);
        return false;
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
      if (!domainsRef.current.length) return;
      const target = event.target as HTMLElement | null;
      if (
        !target?.closest?.(
          '.aui-composer-input, .aui-composer-root, .mcp-domain-mention-prefix'
        )
      ) {
        return;
      }
      if (/(^|[\s\u200b])[@/][^\s]*$/.test(textRef.current)) return;
      event.preventDefault();
      event.stopPropagation();
      mergeAndSend();
    };

    const onClickCapture = (event: MouseEvent) => {
      if (!domainsRef.current.length) return;
      const target = event.target as HTMLElement | null;
      const sendBtn = target?.closest?.(
        '.aui-composer-send, button[aria-label="Send message"]'
      );
      if (!sendBtn) return;
      event.preventDefault();
      event.stopPropagation();
      mergeAndSend();
    };

    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('click', onClickCapture, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [aui, mentions]);

  return null;
};
